import React, { useCallback, useEffect, useState } from 'react';
import type { Event } from '../../types/api';
import EventFinderAPI from '../../services/api';
import { vectorSearchResultToEvent } from '../../utils/vectorSearch';
import EventsExplorer from './EventsExplorer';

const SEARCH_RESULT_LIMIT = 100;

export interface GenericSearchResultsProps {
  query: string;
  title: string;
  description: string;
}

export const GenericSearchResults: React.FC<GenericSearchResultsProps> = ({
  query,
  title,
  description,
}) => {
  const [events, setEvents] = useState<Event[]>([]);
  const [totalAvailable, setTotalAvailable] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reloadToken, setReloadToken] = useState(0);

  useEffect(() => {
    const controller = new AbortController();
    let cancelled = false;

    setEvents([]);
    setTotalAvailable(0);
    setIsLoading(true);
    setError(null);

    (async () => {
      try {
        const response = await EventFinderAPI.vectorSearch({
          searchTerms: query,
          limit: SEARCH_RESULT_LIMIT,
          includeEventDetails: true,
        }, controller.signal);
        if (cancelled) return;

        setEvents(response.results.map(vectorSearchResultToEvent));
        setTotalAvailable(response.totalResults);
      } catch (loadError) {
        if (cancelled || (loadError as Error).name === 'AbortError') return;
        console.error('Failed to load semantic search results', loadError);
        setError(`We could not load these search results. ${(loadError as Error).message || ''}`.trim());
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [query, reloadToken]);

  const handleRetry = useCallback(() => setReloadToken((token) => token + 1), []);

  return (
    <EventsExplorer
      title={title}
      subtitle={description}
      events={events}
      isLoading={isLoading}
      error={error}
      onRetry={handleRetry}
      totalAvailable={totalAvailable}
      fetchLimit={SEARCH_RESULT_LIMIT}
      resultTotal={totalAvailable}
      exportLabel={`Search ${query}`}
      emptyMessage={`No events matched “${query}”.`}
      telemetryCategory="generic_search"
      showRecentlyAdded
      eventGrouping="all"
      primarySectionLabel="Search results"
    />
  );
};

export default GenericSearchResults;